import { Component, computed, inject, linkedSignal, signal, viewChildren } from '@angular/core';
import { Store } from '@ngrx/store';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { CdkOverlayOrigin } from '@angular/cdk/overlay';
import { NotificationV2Service } from '@app/notification-v2.service';
import { submittalsSelectors } from '@app/store/submittals/submittals.selectors';
import { FadedTextComponent } from '@app/framework/faded-text/faded-text.component';
import { StatusBlockComponent } from '@app/pages/webapp/projects/view-project/rfi-listing/status-block/status-block.component';
import { FloatingTextareaComponent } from '@app/framework/inputs/floating-textarea/floating-textarea.component';
import { submittalPackageDetailsActions } from '@app/pages/webapp/projects/view-project/submittals/submittal-package-details-sidebar/submittal-package-details.actions';
import {
  SUBMITTAL_PACKAGE_STATUS,
  SUBMITTAL_REGISTRY_STATUS,
  SUBMITTAL_REGISTRY_STATUS_OPTIONS,
  SubmittalFile,
  SubmittalPackage,
  SubmittalPackageDocumentCategory,
  SubmittalPackageDocumentCategoryOption,
  SubmittalRegistry,
  SubmittalRegistryPartialUpdateDto,
} from '@app/store/submittals/submittals.types';
import { LoadingSpinnerComponent } from '@app/framework/loading-animation/loading-spinner.component';
import { OverlayGeneralComponent } from '@app/framework/overlays/overlay-general/overlay-general.component';
import {
  dropdownOverlayPositions,
  OptionsAction,
} from '@app/framework/overlays/option-list.constants';
import { FileCardComponent } from '@app/framework/file-handling/file-card-component/file-card.component';
import { ArrowButtonBoxComponent } from '@app/framework/dropdown-button-box/arrow-button-box.component';
import { OptionsListSimpleComponent } from '@app/framework/overlays/options-list-simple/options-list-simple.component';
import { OPTIONS } from '@app/framework/constants/options-list.constants';
import { PresignedFileUploadService } from '@app/services/presigned-file-upload.service';
import { UploadHiddenInputComponent } from '@app/framework/file-handling/upload-hidden-input/upload-hidden-input.component';
import { isNotOneOf, isOneOf } from '@app/framework/helpers/general.helper';
import { TooltipDirective } from '@app/directives/tooltip.directive';
import { ManagedFile, UploadMetaData } from '@app/framework/interfaces/file.type';

@Component({
  selector: 'app-submittal-package-submittals-tab',
  imports: [
    FadedTextComponent,
    StatusBlockComponent,
    FloatingTextareaComponent,
    FormsModule,
    LoadingSpinnerComponent,
    OverlayGeneralComponent,
    CdkOverlayOrigin,
    FileCardComponent,
    ArrowButtonBoxComponent,
    OptionsListSimpleComponent,
    UploadHiddenInputComponent,
    TooltipDirective,
  ],
  templateUrl: './submittal-package-submittals-tab.component.html',
  styleUrl: './submittal-package-submittals-tab.component.scss',
})
export class SubmittalPackageSubmittalsTabComponent {
  store = inject(Store);
  notif = inject(NotificationV2Service);
  readonly presignedFileUploadService = inject(PresignedFileUploadService);
  protected readonly SUBMITTAL_PACKAGE_STATUS = SUBMITTAL_PACKAGE_STATUS;

  readonly inputUploadComponent = viewChildren<UploadHiddenInputComponent>(
    'uploadHiddenInputComponent',
  );

  readonly dropdownOverlayPositions = signal(dropdownOverlayPositions).asReadonly();

  readonly submittalPackage = linkedSignal({
    source: toSignal(this.store.select(submittalsSelectors.selectPackageDetails)),
    computation: (submittalPackage) => {
      return submittalPackage;
    },
  });

  readonly editedRegistryEntryData = linkedSignal<SubmittalPackage, Partial<SubmittalRegistry>[]>({
    source: this.submittalPackage,
    computation: (submittalPackage, previousValue) => {
      if (previousValue?.value) {
        return previousValue.value;
      }
      if (!submittalPackage?.entries) {
        return [];
      }
      return submittalPackage.entries.map((entry) => ({ ...entry }));
    },
  });

  readonly isRegistryEntriesLoading = toSignal(
    this.store.select(submittalsSelectors.selectPackageRegistryEntriesLoading),
  );

  packageLockTooltip = computed(() => {
    if (this.submittalPackage().status !== SUBMITTAL_PACKAGE_STATUS.OPEN) {
      return 'Package is not open. Entries cannot be edited.';
    }
    if (this.submittalPackage()?.sent_to_owner_at) {
      return 'Package has been sent to owner. Entries cannot be edited.';
    }
    return null;
  });

  /* Array of booleans indicating whether each entry is in edit mode */
  readonly isEditMode = linkedSignal<SubmittalPackage, boolean[]>({
    source: this.submittalPackage,
    computation: (submittalPackage, previousValue) => {
      if (!submittalPackage?.entries) {
        return [];
      }
      if (previousValue?.value?.length === submittalPackage?.entries?.length) {
        return previousValue.value;
      }

      return this.submittalPackage()?.entries?.map((_) => false);
    },
  });

  readonly isStatusDropdownShown = linkedSignal(() => {
    return this.submittalPackage()?.entries?.map((_) => false);
  });

  readonly statuses = computed<{ label: string; value: SUBMITTAL_REGISTRY_STATUS }[]>(() => {
    const allStatuses = SUBMITTAL_REGISTRY_STATUS_OPTIONS;
    if (this.submittalPackage()?.role === 'manager') {
      return allStatuses.filter((status) => status.value !== SUBMITTAL_REGISTRY_STATUS.NOT_SENT);
    }
    if (this.submittalPackage()?.role === 'approver') {
      return allStatuses.filter((status) =>
        isOneOf<SUBMITTAL_REGISTRY_STATUS>(status.value, [
          SUBMITTAL_REGISTRY_STATUS.APPROVED,
          SUBMITTAL_REGISTRY_STATUS.APPROVED_NOTED,
          SUBMITTAL_REGISTRY_STATUS.REVISE_RESUBMIT,
          SUBMITTAL_REGISTRY_STATUS.REJECTED,
          SUBMITTAL_REGISTRY_STATUS.NOT_REVIEWED,
          SUBMITTAL_REGISTRY_STATUS.VOID,
        ]),
      );
    }
    if (this.submittalPackage()?.role === 'owner') {
      return allStatuses.filter(
        (status) => status.value === SUBMITTAL_REGISTRY_STATUS.FOR_APPROVAL,
      );
    }
  });

  /* Computes the document categories (document columns) to be shown for each user type. */
  protected readonly documentCategories = computed<SubmittalPackageDocumentCategoryOption[]>(() => {
    const role = this.submittalPackage()?.role;
    const packageEditable =
      this.submittalPackage()?.status === SUBMITTAL_PACKAGE_STATUS.OPEN &&
      !this.submittalPackage().sent_to_owner_at;
    const source: SubmittalPackageDocumentCategoryOption = {
      label: 'Source',
      value: 'source',
      canBeEdited: packageEditable && (role === 'manager' || role === 'owner'),
    };
    const reviewed: SubmittalPackageDocumentCategoryOption = {
      label: 'Reviewed',
      value: 'reviewed',
      canBeEdited: packageEditable && (role === 'manager' || role === 'approver'),
    };
    const submitted: SubmittalPackageDocumentCategoryOption = {
      label: 'Submitted',
      value: 'submitted',
      canBeEdited: packageEditable && role === 'manager',
    };

    switch (role) {
      case 'manager':
      case 'participant': {
        return [source, submitted, reviewed];
      }
      case 'approver': {
        return [submitted, reviewed];
      }
      case 'owner': {
        return [source, reviewed];
      }
      default: {
        return [];
      }
    }
  });

  /* Returns a computed array of options available for each category based on roles and file count. */
  protected readonly documentCategoryOptions = computed<OptionsAction<OPTIONS>[][]>(() => {
    const packageNotOpen = this.submittalPackage()?.status !== SUBMITTAL_PACKAGE_STATUS.OPEN;
    return this.documentCategories().map((category) => {
      let options: OptionsAction<OPTIONS>[] = [
        { label: 'Upload', value: OPTIONS.UPLOAD_FILE, disabled: packageNotOpen },
        {
          label: 'Download All',
          value: OPTIONS.DOWNLOAD,
          disabled: !this.submittalPackage()?.files?.[category.value]?.length || packageNotOpen,
        },
        {
          label: 'Delete All',
          value: OPTIONS.DELETE,
          disabled: !this.submittalPackage()?.files?.[category.value]?.length || packageNotOpen,
        },
      ];

      // approvers cannot upload or delete submitted files
      if (this.submittalPackage()?.role === 'approver' && category.value === 'submitted') {
        options = options.filter((option) =>
          isNotOneOf(option.value, [OPTIONS.DELETE, OPTIONS.UPLOAD_FILE]),
        );
      }

      // owners cannot upload or delete reviewed files
      if (this.submittalPackage()?.role === 'owner' && category.value === 'reviewed') {
        options = options.filter((option) =>
          isNotOneOf(option.value, [OPTIONS.DELETE, OPTIONS.UPLOAD_FILE]),
        );
      }

      // participants cannot upload or delete files at all
      if (this.submittalPackage()?.role === 'participant') {
        options = options.filter((option) =>
          isNotOneOf(option.value, [OPTIONS.DELETE, OPTIONS.UPLOAD_FILE]),
        );
      }

      return options;
    });
  });

  protected readonly optionsListShown = linkedSignal(() => {
    return this.documentCategories()?.map((_) => false);
  });

  protected readonly lockOptionsShown = linkedSignal(() => {
    return this.submittalPackage()?.entries?.map((_) => false);
  });

  protected readonly lockOptions = signal<OptionsAction<OPTIONS>[]>([
    { label: 'Unlock entry', value: OPTIONS.UNLOCK },
    { label: 'Edit entry', value: OPTIONS.EDIT },
  ]).asReadonly();

  protected enterEditMode(index: number) {
    this.isEditMode.update((oldValue) => {
      return oldValue.map((value, i) => (i === index ? true : value));
    });
  }

  saveEntry(index: number) {
    this.isEditMode.update((oldValue) => {
      return oldValue.map((value, i) => (i === index ? false : value));
    });

    const editedEntry = this.editedRegistryEntryData()?.[index];
    if (!editedEntry) {
      this.notif.showError('Failed to save entry: Entry not found.');
      return;
    }

    const submittalRegistryPayload: SubmittalRegistryPartialUpdateDto = {
      note: editedEntry.note,
      status: editedEntry.status,
    };

    this.store.dispatch(
      submittalPackageDetailsActions.saveEntryClicked({
        registryEntryId: editedEntry.id,
        registryEntryPayload: submittalRegistryPayload,
      }),
    );
  }

  protected cancelEdit(index: number) {
    this.isEditMode.update((oldValue) => {
      return oldValue.map((value, i) => (i === index ? false : value));
    });

    this.editedRegistryEntryData.update((oldValues) => {
      const originalEntry = this.submittalPackage()?.entries?.[index];
      if (!originalEntry) {
        this.notif.showError('Failed to cancel edit: Original entry not found.');
        return oldValues;
      }
      return oldValues.map((oldEntry, i) => (i === index ? originalEntry : oldEntry));
    });
  }

  statusBlockClicked(index: number) {
    if (this.isEditMode()[index]) {
      this.isStatusDropdownShown.update((oldValue) => {
        return oldValue.map((value, i) => (i === index ? !value : value));
      });
    }
  }

  toggleStatusDropdown(index: number) {
    this.isStatusDropdownShown.update((oldValue) => {
      return oldValue.map((value, i) => (i === index ? !value : value));
    });
  }

  noteChanged(index: number, newValue: string) {
    this.updateRegistryEntry(index, { note: newValue });
  }
  statusSelected(index: number, newValue: SUBMITTAL_REGISTRY_STATUS) {
    this.isStatusDropdownShown.update((oldValues) => {
      return oldValues.map((value, i) => (i === index ? false : value));
    });
    this.updateRegistryEntry(index, { status: newValue });
  }

  /**
   * Updates locally stored registry entry data in both
   * @param index
   * @param registryEntry
   * @private
   */
  private updateRegistryEntry(index: number, registryEntry: Partial<SubmittalRegistry>) {
    if (!this.editedRegistryEntryData()?.[index]) {
      this.notif.showError('Failed to save entry: Entry not found.');
      return;
    }

    this.editedRegistryEntryData.update((oldValues) => {
      return oldValues.map((oldEntry, i) =>
        i === index ? { ...oldEntry, ...registryEntry } : oldEntry,
      );
    });
  }

  optionsListShowChange(index: number, shown: boolean) {
    this.optionsListShown.update((oldValue) => {
      return oldValue.map((value, i) => (i === index ? shown : value));
    });
  }

  protected optionSelected(
    documentCategory: SubmittalPackageDocumentCategory,
    index: number,
    selectedOption: OptionsAction,
  ) {
    switch (selectedOption.value) {
      case OPTIONS.UPLOAD_FILE: {
        this.inputUploadComponent()[index].openFileUploadWindow();
        break;
      }
      case OPTIONS.DELETE: {
        this.deleteAllFilesInCategory(documentCategory);
        break;
      }
      case OPTIONS.DOWNLOAD: {
        this.store.dispatch(
          submittalPackageDetailsActions.downloadAllFilesInCategorySelected({
            packageId: this.submittalPackage()?.id,
            documentCategory,
          }),
        );
        break;
      }
    }
  }

  private deleteAllFilesInCategory(documentCategory: SubmittalPackageDocumentCategory) {
    this.notif
      .showConfirmDialog('Are you sure you want to delete all files in this category?')
      .subscribe((resp) => {
        if (resp) {
          this.store.dispatch(
            submittalPackageDetailsActions.deleteAllFilesInCategorySelected({
              packageId: this.submittalPackage()?.id,
              documentCategory,
            }),
          );
        }
      });
  }

  protected filesChanged(documentCategory: SubmittalPackageDocumentCategory, files: ManagedFile[]) {
    const metadatas: Partial<UploadMetaData>[] = files.map(() => ({
      submittal_package: {
        doc_type: documentCategory,
        id: this.submittalPackage()?.id,
      },
    }));

    this.presignedFileUploadService
      .uploadMultipleFilesToS3(files, metadatas)
      .subscribe((uploadedFiles) => {
        const someUploadsFailed = uploadedFiles.some((file) => file.error);
        if (someUploadsFailed) {
          this.store.dispatch(
            submittalPackageDetailsActions.uploadFilesToCategoryFailure({
              packageId: this.submittalPackage()?.id,
              documentCategory,
            }),
          );
          return;
        }

        this.store.dispatch(
          submittalPackageDetailsActions.uploadFilesToCategorySuccess({
            packageId: this.submittalPackage()?.id,
            documentCategory,
            files: uploadedFiles,
          }),
        );
      });
  }

  deleteFile(file: SubmittalFile, event: MouseEvent) {
    event.stopPropagation();
    if (this.submittalPackage()?.status !== 'open' || this.submittalPackage()?.sent_to_owner_at) {
      return;
    }
    this.notif.showConfirmDialog('Are you sure you want to delete this file?').subscribe((resp) => {
      if (resp) {
        this.store.dispatch(
          submittalPackageDetailsActions.deleteFileClicked({
            packageId: this.submittalPackage()?.id,
            fileId: file.id,
          }),
        );
      }
    });
  }

  protected toggleEntryLockStatus(
    registryEntry: Partial<SubmittalRegistry>,
    index: number,
    event: MouseEvent,
  ) {
    if (this.submittalPackage().role !== 'manager') {
      event.stopPropagation();
      return;
    }
  }

  protected lockOptionsShowChange(index: number, newValue: boolean) {
    this.lockOptionsShown.update((oldValues) => {
      return oldValues.map((value, i) => (i === index ? newValue : value));
    });
  }

  protected lockOptionSelected(index: number, option: OptionsAction<string>) {
    const optionSelected = option as OptionsAction<OPTIONS>;
    switch (optionSelected.value) {
      case OPTIONS.UNLOCK: {
        this.notif
          .showConfirmDialog('Are you sure you want to unlock this entry?')
          .subscribe((resp) => {
            if (resp) {
              this.store.dispatch(
                submittalPackageDetailsActions.unlockEntryClicked({
                  registryEntryId: this.submittalPackage()?.entries?.[index]?.id,
                }),
              );
            }
          });
        break;
      }
      case OPTIONS.EDIT: {
        this.enterEditMode(index);
        break;
      }
    }
  }
}
